import { useSyncExternalStore } from 'react';

/**
 * Are we online? Drives the offline honesty layer (PLAN §Offline).
 *
 * `navigator.onLine` + the `online`/`offline` events. This is exactly the signal
 * AC-4 exercises ("turn the network off"): toggling the OS/DevTools network state
 * fires `offline` and flips `navigator.onLine` to false reliably. It is a coarse
 * signal (a live TCP connection to a dead server can still read `onLine: true`),
 * but for "the device has no network" — the case where the map must stop
 * pretending to be live — it is the right and simplest one. Socket
 * reconnect/degraded states are handled separately by the map's own banners.
 *
 * `useSyncExternalStore` so the value is read consistently and every subscriber
 * re-renders on change, with no effect-timing gap.
 */

function subscribe(onChange: () => void): () => void {
  window.addEventListener('online', onChange);
  window.addEventListener('offline', onChange);
  return () => {
    window.removeEventListener('online', onChange);
    window.removeEventListener('offline', onChange);
  };
}

function getSnapshot(): boolean {
  // Default to online when the API is unavailable (SSR/old engines): the honest
  // default is "assume connected", so we never falsely cover a live map in the
  // offline treatment.
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

export function useOnline(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, () => true);
}
